/**
 * Single Clerk indirection. Every app file imports `useAuth`, `useUser`,
 * `useClerk`, `SignIn`, `UserButton`, and `ClerkProvider` from here instead
 * of from `@clerk/react` directly so tests and mocked-service E2E can swap
 * the implementation through one seam.
 *
 * Test-time replacement: set `window.__chancey_test_auth__` to an object
 * with a subset of the Clerk surface BEFORE the app bundle loads. Anything
 * unset falls through to the real `@clerk/react`. Webpack-alias seam
 * approaches are not viable in this codebase without CRACO or ejecting
 * react-scripts.
 *
 * Unit tests that prefer `jest.mock('@clerk/react', ...)` continue to work
 * unchanged because this module imports from `@clerk/react`.
 */
import React, { type ComponentType } from 'react';
import * as ClerkReact from '@clerk/react';
import { useNativeAuthOptional } from './nativeAuth';

declare global {
  interface Window {
    __chancey_test_auth__?: Partial<{
      useAuth: typeof ClerkReact.useAuth;
      useUser: typeof ClerkReact.useUser;
      useClerk: typeof ClerkReact.useClerk;
      useSignIn: typeof ClerkReact.useSignIn;
      useSignUp: typeof ClerkReact.useSignUp;
      SignIn: ComponentType<unknown>;
      AuthenticateWithRedirectCallback: ComponentType<AuthenticateWithRedirectCallbackProps>;
      UserButton: ComponentType<unknown>;
      ClerkProvider: ComponentType<{ children?: React.ReactNode }>;
    }>;
  }
}

const shim =
  typeof window !== 'undefined' && window.__chancey_test_auth__
    ? window.__chancey_test_auth__
    : ({} as NonNullable<Window['__chancey_test_auth__']>);

export const useAuth = ((...args: Parameters<typeof ClerkReact.useAuth>) => {
  if (shim.useAuth) return shim.useAuth(...args);
  const nativeAuth = useNativeAuthOptional();
  const webAuth = ClerkReact.useAuth(...args);
  if (!nativeAuth?.isNativePlatform) return webAuth;
  if (!nativeAuth.isAvailable) {
    return {
      ...webAuth,
      isLoaded: nativeAuth.isLoaded,
      isSignedIn: false,
      userId: null,
      sessionId: null,
      getToken: async () => null,
      signOut: async () => undefined,
    };
  }
  return {
    ...webAuth,
    isLoaded: nativeAuth.isLoaded,
    isSignedIn: nativeAuth.isSignedIn,
    userId: nativeAuth.user?.id ?? null,
    sessionId: nativeAuth.isSignedIn ? 'native' : null,
    getToken: nativeAuth.getToken,
    signOut: nativeAuth.signOut,
  };
}) as typeof ClerkReact.useAuth;

export const useUser = ((...args: Parameters<typeof ClerkReact.useUser>) => {
  if (shim.useUser) return shim.useUser(...args);
  const nativeAuth = useNativeAuthOptional();
  const webUser = ClerkReact.useUser(...args);
  if (!nativeAuth?.isNativePlatform) return webUser;
  if (!nativeAuth.isAvailable) {
    return {
      ...webUser,
      isLoaded: nativeAuth.isLoaded,
      isSignedIn: false,
      user: null,
    };
  }
  return {
    ...webUser,
    isLoaded: nativeAuth.isLoaded,
    isSignedIn: nativeAuth.isSignedIn,
    user: nativeAuth.user,
  };
}) as typeof ClerkReact.useUser;

export const useClerk = ((...args: Parameters<typeof ClerkReact.useClerk>) => {
  if (shim.useClerk) return shim.useClerk(...args);
  const nativeAuth = useNativeAuthOptional();
  const webClerk = ClerkReact.useClerk(...args);
  if (!nativeAuth?.isNativePlatform) return webClerk;
  if (!nativeAuth.isAvailable) {
    return {
      ...webClerk,
      loaded: nativeAuth.isLoaded,
      user: null,
      signOut: async () => undefined,
      openUserProfile: async () => undefined,
    };
  }
  return {
    ...webClerk,
    loaded: nativeAuth.isLoaded,
    user: nativeAuth.user,
    signOut: nativeAuth.signOut,
    openUserProfile: nativeAuth.openUserProfile,
  };
}) as typeof ClerkReact.useClerk;

export const useSignIn: typeof ClerkReact.useSignIn =
  (shim.useSignIn as typeof ClerkReact.useSignIn) ?? ClerkReact.useSignIn;

export const useSignUp: typeof ClerkReact.useSignUp =
  (shim.useSignUp as typeof ClerkReact.useSignUp) ?? ClerkReact.useSignUp;

export const SignIn = (shim.SignIn ?? ClerkReact.SignIn) as typeof ClerkReact.SignIn;

export interface AuthenticateWithRedirectCallbackProps {
  signInForceRedirectUrl?: string | null;
  signInFallbackRedirectUrl?: string | null;
  signUpForceRedirectUrl?: string | null;
  signUpFallbackRedirectUrl?: string | null;
  signInUrl?: string;
  signUpUrl?: string;
}

export const AuthenticateWithRedirectCallback = (shim.AuthenticateWithRedirectCallback ??
  ClerkReact.AuthenticateWithRedirectCallback) as unknown as ComponentType<AuthenticateWithRedirectCallbackProps>;

const NativeAwareUserButton: ComponentType<unknown> = (props) => {
  const nativeAuth = useNativeAuthOptional();
  if (!nativeAuth?.isNativePlatform) {
    return React.createElement(
      ClerkReact.UserButton as ComponentType<Record<string, unknown>>,
      props as Record<string, unknown>
    );
  }
  if (!nativeAuth.isAvailable) return null;

  const first = nativeAuth.user?.firstName?.trim()?.[0] ?? '';
  const last = nativeAuth.user?.lastName?.trim()?.[0] ?? '';
  const fallback = `${first}${last}` || nativeAuth.user?.primaryEmailAddress?.emailAddress?.[0] || '?';
  return React.createElement(
    'button',
    {
      type: 'button',
      'aria-label': 'Manage account',
      onClick: () => void nativeAuth.openUserProfile(),
      style: {
        alignItems: 'center',
        background: 'transparent',
        border: 0,
        borderRadius: 999,
        display: 'inline-flex',
        height: 40,
        justifyContent: 'center',
        padding: 0,
        width: 40,
      },
    },
    nativeAuth.user?.imageUrl
      ? React.createElement('img', {
          alt: '',
          src: nativeAuth.user.imageUrl,
          style: {
            borderRadius: 999,
            height: 40,
            objectFit: 'cover',
            width: 40,
          },
        })
      : React.createElement(
          'span',
          {
            style: {
              alignItems: 'center',
              background: '#111',
              borderRadius: 999,
              color: '#fff',
              display: 'inline-flex',
              fontSize: 13,
              fontWeight: 700,
              height: 40,
              justifyContent: 'center',
              textTransform: 'uppercase',
              width: 40,
            },
          },
          fallback
        )
  );
};

export const UserButton = (shim.UserButton ?? NativeAwareUserButton) as typeof ClerkReact.UserButton;

export const ClerkProvider = (shim.ClerkProvider ??
  ClerkReact.ClerkProvider) as typeof ClerkReact.ClerkProvider;
